import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip,
} from '@mui/material';
import { UserDataContext } from '../../context/UserDataContext';
import CasinoIcon from '@mui/icons-material/Casino';
import InfoIcon from '@mui/icons-material/Info';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LockIcon from '@mui/icons-material/Lock';

// Singleton socket instance
let socketInstance = null;

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
  lightGreen: '#E8F5E9',
};

const GameMaster = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [game, setGame] = useState({ players: [], calledNumbers: [], autoMark: false, gameEnded: false, winners: [] });
  const [number, setNumber] = useState('');
  const [letter, setLetter] = useState('C');
  const [isAutoCalling, setIsAutoCalling] = useState(false);
  const [autoMark, setAutoMark] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState({
    C: Array.from({ length: 15 }, (_, i) => i + 1),
    R: Array.from({ length: 15 }, (_, i) => i + 16),
    O: Array.from({ length: 15 }, (_, i) => i + 31),
    W: Array.from({ length: 15 }, (_, i) => i + 46),
    N: Array.from({ length: 15 }, (_, i) => i + 61),
  });

  const ranges = {
    C: [1, 15],
    R: [16, 30],
    O: [31, 45],
    W: [46, 60],
    N: [61, 75],
  };

  const isAdmin = true; // Example: Check if user is admin

  useEffect(() => {
    if (!isAdmin) return;

    if (!socketInstance) {
      socketInstance = io('https://bosscrowns-api-a228488a1e46.herokuapp.com', {
        autoConnect: false,
        query: { role: 'game-master' },
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });
    }

    socketInstance.connect();
    console.log(`GameMaster connected to socket, joined game ${pin}`);

    socketInstance.emit('join-game', pin);

    const handleGameState = ({ calledNumbers, autoMark, gameEnded, winners }) => {
      console.log(`Received game-state for ${pin}:`, { calledNumbers, autoMark, gameEnded, winners });
      setGame((prev) => ({ ...prev, calledNumbers, autoMark, gameEnded, winners }));
      setAutoMark(autoMark);
      if (gameEnded || winners.length > 0) {
        setIsAutoCalling(false);
      }
      setAvailableNumbers((prev) => {
        const newNumbers = {
          C: Array.from({ length: 15 }, (_, i) => i + 1),
          R: Array.from({ length: 15 }, (_, i) => i + 16),
          O: Array.from({ length: 15 }, (_, i) => i + 31),
          W: Array.from({ length: 15 }, (_, i) => i + 46),
          N: Array.from({ length: 15 }, (_, i) => i + 61),
        };
        calledNumbers.forEach((combo) => {
          const letter = combo[0];
          const num = parseInt(combo.slice(1));
          newNumbers[letter] = newNumbers[letter].filter((n) => n !== num);
        });
        return newNumbers;
      });
    };

    const handlePlayerJoined = (players) => {
      console.log(`Players joined in ${pin}:`, players);
      setGame((prev) => ({ ...prev, players }));
    };

    const handleNumberCalled = ({ number, letter, gameEnded }) => {
      console.log(`Number called in ${pin}: ${letter}${number}`);
      const combo = `${letter}${number}`;
      setGame((prev) => {
        if (prev.calledNumbers.includes(combo)) {
          console.warn(`Number ${combo} already in calledNumbers, skipping update`);
          return prev;
        }
        return {
          ...prev,
          calledNumbers: [...prev.calledNumbers, combo],
          gameEnded,
        };
      });
      setAvailableNumbers((prev) => ({
        ...prev,
        [letter]: prev[letter].filter((n) => n !== Number(number)),
      }));
    };

    const handlePlayerWon = ({ playerName, playerEmail, gameEnded }) => {
      console.log(`Player won in ${pin}: ${playerName} (${playerEmail})`);
      setGame((prev) => ({
        ...prev,
        winners: [{ name: playerName, email: playerEmail }],
        gameEnded: true,
      }));
      setIsAutoCalling(false);
    };

    const handleError = ({ message }) => {
      console.error(`Socket error in ${pin}: ${message}`);
      setIsLoading(false);
    };

    socketInstance.on('game-state', handleGameState);
    socketInstance.on('player-joined', handlePlayerJoined);
    socketInstance.on('number-called', handleNumberCalled);
    socketInstance.on('player-won', handlePlayerWon);
    socketInstance.on('error', handleError);

    const fetchGame = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://bosscrowns-api-a228488a1e46.herokuapp.com/bingo/${pin}`);
        if (!response.ok) throw new Error('Game not found');
        const data = await response.json();
        console.log(`Fetched game data for ${pin}:`, data);
        setGame(data);
        setAutoMark(data.autoMark || false);
        setAvailableNumbers((prev) => {
          const newNumbers = {
            C: Array.from({ length: 15 }, (_, i) => i + 1),
            R: Array.from({ length: 15 }, (_, i) => i + 16),
            O: Array.from({ length: 15 }, (_, i) => i + 31),
            W: Array.from({ length: 15 }, (_, i) => i + 46),
            N: Array.from({ length: 15 }, (_, i) => i + 61),
          };
          data.calledNumbers.forEach((combo) => {
            const letter = combo[0];
            const num = parseInt(combo.slice(1));
            newNumbers[letter] = newNumbers[letter].filter((n) => n !== num);
          });
          return newNumbers;
        });
      } catch (err) {
        console.error(`Error fetching game ${pin}:`, err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGame();

    return () => {
      console.log(`Cleaning up GameMaster socket for ${pin}`);
      if (socketInstance) {
        socketInstance.off('game-state', handleGameState);
        socketInstance.off('player-joined', handlePlayerJoined);
        socketInstance.off('number-called', handleNumberCalled);
        socketInstance.off('player-won', handlePlayerWon);
        socketInstance.off('error', handleError);
        socketInstance.disconnect();
        socketInstance = null;
      }
    };
  }, [pin, isAdmin]);

  const callRandomNumber = useCallback(() => {
    if (game.gameEnded) {
      setIsAutoCalling(false);
      return;
    }
    const letters = ['C', 'R', 'O', 'W', 'N'];
    const availableLetters = letters.filter((ltr) => availableNumbers[ltr].length > 0);
    if (availableLetters.length === 0) {
      setIsAutoCalling(false);
      return;
    }

    const selectedLetter = availableLetters[Math.floor(Math.random() * availableLetters.length)];
    const numbers = availableNumbers[selectedLetter];
    const randomIndex = Math.floor(Math.random() * numbers.length);
    const selectedNumber = numbers[randomIndex];

    console.log(`Emitting call-number: ${selectedLetter}${selectedNumber} for pin ${pin}`);
    socketInstance.emit('call-number', { pin, number: selectedNumber, letter: selectedLetter });

    setAvailableNumbers((prev) => ({
      ...prev,
      [selectedLetter]: prev[selectedLetter].filter((num) => num !== selectedNumber),
    }));
  }, [pin, availableNumbers, game.gameEnded]);

  const toggleAutoMark = useCallback(() => {
    if (!isAdmin) {
      return;
    }
    if (game.gameEnded) {
      return;
    }
    const newAutoMark = !autoMark;
    setAutoMark(newAutoMark);
    console.log(`Emitting set-auto-mark: ${newAutoMark} for pin ${pin}`);
    socketInstance.emit('set-auto-mark', { pin, autoMark: newAutoMark });
  }, [pin, autoMark, game.gameEnded, isAdmin]);

  const toggleAutoCall = useCallback(() => {
    if (!isAdmin) {
      return;
    }
    if (game.gameEnded) {
      return;
    }
    setIsAutoCalling((prev) => !prev);
  }, [game.gameEnded, isAdmin]);

  const callNumber = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    if (game.gameEnded) {
      return;
    }
    const num = Number(number);
    const [min, max] = ranges[letter];
    if (!number || isNaN(num) || num < min || num > max || !availableNumbers[letter].includes(num)) {
      return;
    }
    setIsLoading(true);
    try {
      console.log(`Emitting call-number: ${letter}${num} for pin ${pin}`);
      socketInstance.emit('call-number', { pin, number: num, letter });
      setNumber('');
    } catch (err) {
      console.error(`Error calling number ${letter}${num}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [pin, number, letter, availableNumbers, game.gameEnded, isAdmin]);

  const startNewGame = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log(`Emitting start-new-game for pin ${pin}`);
      socketInstance.emit('start-new-game', pin);
      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate('/bingo/create');
    } catch (err) {
      console.error(`Error starting new game for ${pin}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [pin, navigate]);

  useEffect(() => {
    let interval;
    if (isAutoCalling && !game.gameEnded) {
      interval = setInterval(() => {
        callRandomNumber();
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isAutoCalling, callRandomNumber, game.gameEnded]);

  if (!isAdmin) {
    return (
      <Container
        maxWidth="md"
        sx={{
          py: { xs: 3, sm: 5 },
          background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.background} 100%)`,
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: { xs: 1, sm: 2 } }}>
            <LockIcon sx={{ color: theme.primary, fontSize: { xs: '1.5rem', sm: '2rem' }, mr: 1 }} />
            <Typography
              variant="h6"
              sx={{
                color: theme.text,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Access Denied: Only admins can access this page.
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, sm: 5 },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          bgcolor: theme.background,
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <CasinoIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: theme.primary, mb: 2 }} />
          <Typography
            variant="h4"
            sx={{
              color: theme.text,
              fontWeight: 'bold',
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.8rem', sm: '2.5rem' },
            }}
          >
            Game Master Dashboard
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.text,
              mb: 2,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Game PIN: {pin}
          </Typography>
          {game.gameEnded && game.winners.length > 0 && (
            <Typography
              variant="h4"
              sx={{
                color: theme.primary,
                fontWeight: 'bold',
                mb: 3,
                fontSize: { xs: '2rem', sm: '3rem' },
              }}
            >
              Winner: {game.winners[0].name} - {game.winners[0].email}
            </Typography>
          )}
          {isLoading && <CircularProgress sx={{ mb: 2 }} />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
              width: '100%',
              maxWidth: 600,
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                sx={{
                  '&.Mui-focused': { color: theme.primary },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Letter ({ranges[letter][0]}-{ranges[letter][1]})
              </InputLabel>
              <Select
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                disabled={isAutoCalling || game.gameEnded || isLoading}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.text,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.primary,
                  },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                {['C', 'R', 'O', 'W', 'N'].map((ltr) => (
                  <MenuItem key={ltr} value={ltr}>
                    {ltr} ({ranges[ltr][0]}-{ranges[ltr][1]})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Number"
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={isAutoCalling || game.gameEnded || isLoading}
              inputProps={{ min: ranges[letter][0], max: ranges[letter][1] }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.primary,
                  },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&.Mui-focused': { color: theme.primary },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={callNumber}
              disabled={isAutoCalling || game.gameEnded || isLoading}
              sx={{
                bgcolor: theme.primary,
                '&:hover': { bgcolor: theme.secondary },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              Call Number
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              onClick={callRandomNumber}
              disabled={isAutoCalling || game.gameEnded || isLoading}
              startIcon={<CasinoIcon />}
              sx={{
                bgcolor: theme.primary,
                '&:hover': { bgcolor: theme.secondary },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              Call Random
            </Button>
            <Button
              variant="contained"
              onClick={toggleAutoCall}
              disabled={game.gameEnded || isLoading}
              startIcon={isAutoCalling ? <StopIcon /> : <PlayArrowIcon />}
              sx={{
                bgcolor: theme.primary,
                '&:hover': { bgcolor: theme.secondary },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              {isAutoCalling ? 'Stop Auto-Call' : 'Start Auto-Call'}
            </Button>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoMark}
                onChange={toggleAutoMark}
                disabled={game.gameEnded || isLoading}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.secondary,
                  },
                }}
              />
            }
            label="Auto-Mark for Players"
            sx={{ mb: 3, color: theme.text }}
          />
          <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.text,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Called Numbers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {game.calledNumbers.map((num, idx) => (
                <Chip
                  key={idx}
                  label={num}
                  sx={{
                    bgcolor: theme.primary,
                    color: theme.background,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.text,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Players
            </Typography>
            <List dense>
              {game.players.map((player, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: theme.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={player.name}
                    secondary={player.email}
                    primaryTypographyProps={{ color: theme.text }}
                    secondaryTypographyProps={{ color: theme.text }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          
          <Button
            variant="contained"
            onClick={startNewGame}
            disabled={isLoading}
            sx={{
              bgcolor: theme.primary,
              '&:hover': { bgcolor: theme.secondary },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Start New Game
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default GameMaster;